// mission-blueprint-committed-bytes-check.ts
//
// The init-time half of the B3 blueprint binding, split out of
// mission-blueprint-binding.ts (close-out 2026-09-30, H20 pushed that file past
// the 200-line budget). `verifyDeclaredBlueprintAtInit` runs BEFORE any side
// effect (no control root, no lock, no journal intent — a rejected init must
// leave no `missions/<id>/` behind, or `resolveMission` sees two missions
// forever). [B1] It reads the bytes COMMITTED at `rev` (default HEAD; preflight
// passes the request's validated `base_sha`) — the commit the mission worktree
// is actually checked out from — never the working tree, which may be dirty,
// and never HEAD when HEAD has since moved past base_sha. The declared hash
// must match those bytes, and spec §13's non-goals rule is applied to them.
// H20: no blueprint at `rev` is a refusal, not a skip; only the empty-repo
// null-oid path (nothing committed anywhere) bypasses this check, in the
// preflight.
//
// `git show` goes through `execFile` with `encoding: "buffer"` on purpose:
// `spawnAsyncCapture` concatenates `chunk.toString()` per chunk, which can
// split a multi-byte character across chunks and change the bytes being
// hashed. The hash contract is raw bytes, so raw bytes it is.

import { execFile } from "node:child_process";
import { posix } from "node:path";
import { promisify } from "node:util";
import { assertNonGoalsPresent, sha256HexOfBytes } from "./blueprint-gate-hash.js";
import { validateBlueprintSourcePath } from "./mission-blueprint-binding.js";

const execFileAsync = promisify(execFile);

/** Largest blueprint `git show` will hand back before we call it unreadable. */
const MAX_BLUEPRINT_BYTES = 16 * 1024 * 1024;

/**
 * Bytes of `sourcePath` as committed at `rev` (default `"HEAD"`) of
 * `sourceRepo`, or `undefined` when `rev` is unborn or the path is not in its
 * tree. The working tree is never consulted: the mission worktree is created
 * from a specific commit, not from whatever is checked out.
 */
export async function loadCommittedBlueprintBytes(
  sourceRepo: string,
  sourcePath: string,
  rev = "HEAD",
): Promise<Buffer | undefined> {
  const spec = `${rev}:${posix.normalize(sourcePath.replace(/\\/g, "/"))}`;
  try {
    const { stdout } = await execFileAsync("git", ["show", spec], {
      cwd: sourceRepo,
      encoding: "buffer",
      maxBuffer: MAX_BLUEPRINT_BYTES,
    });
    return stdout;
  } catch {
    // exit 128 for both "unborn HEAD" and "path not in HEAD" — no committed
    // blueprint either way. A missing git binary surfaces one step later,
    // when the initializer creates the worktree.
    return undefined;
  }
}

export interface VerifyDeclaredBlueprintInput {
  sourceRepo: string;
  sourcePath: string;
  declaredSha256: string;
  /** Commit mà worktree mission dựng từ đó; chỉ nhận sha đã validate ở preflight. Mặc định "HEAD". */
  rev?: string;
}

/**
 * Problem message when the init request's blueprint declaration cannot be
 * trusted (including: not committed at `rev`); `undefined` when it can.
 */
export async function verifyDeclaredBlueprintAtInit(
  input: VerifyDeclaredBlueprintInput,
): Promise<string | undefined> {
  const pathProblem = validateBlueprintSourcePath(input.sourcePath);
  if (pathProblem !== undefined) return pathProblem;

  const rev = input.rev ?? "HEAD";
  const bytes = await loadCommittedBlueprintBytes(input.sourceRepo, input.sourcePath, rev);
  if (bytes === undefined) {
    // H20 (close-out 2026-09-30): absent used to mean "nothing to check" ⇒
    // init exit 0 with G0 bound to a hash of bytes the worktree never holds,
    // and a worker that never sees the blueprint's non-goals. Callers reach
    // here only once `rev` is a real commit (preflight (c)); the empty-repo
    // null-oid path returns before this function.
    return (
      `BLUEPRINT "${input.sourcePath}" không có (hoặc git không đọc được) trong commit ${rev} (${rev}:${input.sourcePath}) — ` +
      "mission worktree dựng từ commit đó nên worker sẽ không thấy BLUEPRINT/Ngoài phạm vi. " +
      "Commit BLUEPRINT rồi init lại với base_sha là commit chứa nó."
    );
  }

  const actual = sha256HexOfBytes(bytes);
  const declared = input.declaredSha256.toLowerCase();
  if (actual !== declared) {
    return (
      `blueprint.bytes_sha256 khai (${declared}) không khớp bytes đã commit ở ` +
      `${rev}:${input.sourcePath} (${actual}). Tính lại hash trên đúng file đã commit ` +
      "(shasum -a 256) rồi init lại — G0 chỉ được gắn với bytes mission thật sự thấy."
    );
  }
  return assertNonGoalsPresent(bytes.toString("utf8"));
}
